import React, { useCallback } from "react";
import Button from "@material-ui/core/Button";
import AddOutlined from "@material-ui/icons/AddOutlined";
import { productDetailsAction } from "../presentation/productCopy";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { setProduct } from "../../../store/actions";
import { registerGlanceView } from "../../../store/actions/analytics";
import { useStorefrontFoundation } from "../StorefrontFoundationBoundary";
import { isStorefrontFoundationEnabled } from "../featureFlags";
import { resolveStorefrontPreset } from "../presentation/presets";
import { getOriginalProduct, getPrepTime } from "../../Menu/Helpers";
import QuickAddToCart from "../../Menu/QuickAddToCart";
import { trackSearchResult } from "../../../Helpers/marketingEvents";
import FavoriteProductIcon from "../../ProductDetails/FavoriteProducts/FavoriteProductIcon";
import StockWarningMessage from "../../ProductDetails/StockWarningMessage";
import MerchantTagBadge from "./MerchantTagBadge";
import PreparationTimeToken from "./PreparationTimeToken";
import SharedProductCard from "./SharedProductCard";
import {
  resolveMerchantTag,
  resolvePreparationTime,
  resolveStockWarning,
  shouldShowFavoriteControl,
} from "./parity";
import { createStorefrontCardPriceQuote } from "./priceQuote";
import {
  canUseStorefrontQuickAdd,
  shouldShowStorefrontDetailsAction,
} from "./quickAdd";

const getProductPath = (product) => {
  if (!product) return "/";
  const category = product.category || product.category_slug;
  if (!category || !product.slug) return `/product/${product.id}`;
  return product.parent
    ? `/product/${category}/${product.parent}/${product.slug}`
    : `/product/${category}/${product.slug}`;
};

const withStorefrontProductCard = (LegacyComponent, { surface }) => {
  const FoundationProductCard = withRouter((props) => {
    const { foundation } = props;
    const dispatch = useDispatch();
    const state = useSelector((rootState) => ({
      settings: rootState.store.store.settings || {},
      language: rootState.store.language,
      cart: rootState.cart.cart || [],
      coupon: rootState.cart.promos,
      discounts: rootState.cart.discounts,
      smartPromos: rootState.store.store.smart_promos || [],
      categories: rootState.store.store.categories || [],
      tags: rootState.store.store.tags || [],
      usedComponents: rootState.store.used_components || {},
      user: rootState.auth?.user || null,
    }), shallowEqual);

    const quickAddType = ["list", "jumbo"].includes(surface)
      ? "list"
      : ["grid", "bubbles"].includes(surface)
      ? "grid"
      : undefined;

    const getPriceQuote = useCallback(
      (product) =>
        createStorefrontCardPriceQuote({
          coupon: state.coupon,
          discounts: state.discounts,
          product,
          settings: state.settings,
          smartPromos: state.smartPromos,
        }),
      [
        state.coupon,
        state.discounts,
        state.settings,
        state.smartPromos,
      ],
    );

    const activateProduct = (selectedProduct, variantState = {}) => {
      if (props.filter_value) {
        trackSearchResult({ ...props, product: selectedProduct, settings: state.settings });
      }
      if (props.fromRecommendations) {
        dispatch(
          registerGlanceView({
            type: "product_recommendation_click",
            source: props.fromCart
              ? "cart"
              : props.fromCheckout
              ? "checkout"
              : "product details",
          }),
        );
      }
      dispatch(setProduct(selectedProduct));
      const navigate = props.fromRecommendations
        ? props.history.replace
        : props.history.push;
      navigate(getProductPath(selectedProduct), {
        deliveryOnly: selectedProduct.delivery_only,
        pickupOnly: selectedProduct.pickup_only,
        storefrontVariantChildId: variantState.defaultChildId || null,
        storefrontVariantPriceSummary: variantState.priceSummary || null,
        storefrontVariantSelection: variantState.selection || {},
        storefrontVariantSummary: props.product?.variant_summary || null,
      });
    };

    return (
      <SharedProductCard
        action={(selectedProduct, variantState) => {
          const actionProduct = getOriginalProduct(
            selectedProduct,
            props.category_id,
            state.categories,
          );
          const cardProduct = props.product || {};
          const quickAddProduct = {
            ...actionProduct,
            extra_fields: Array.isArray(actionProduct?.extra_fields) &&
              actionProduct.extra_fields.length
              ? actionProduct.extra_fields
              : Array.isArray(cardProduct.extra_fields)
              ? cardProduct.extra_fields
              : [],
            has_required_extra_fields:
              actionProduct?.has_required_extra_fields === true ||
              cardProduct.has_required_extra_fields === true,
            has_required_options:
              actionProduct?.has_required_options === true ||
              cardProduct.has_required_options === true,
            options: Array.isArray(actionProduct?.options) &&
              actionProduct.options.length
              ? actionProduct.options
              : Array.isArray(cardProduct.options)
              ? cardProduct.options
              : [],
            show_quick_add_to_cart:
              actionProduct?.show_quick_add_to_cart === true ||
              cardProduct.show_quick_add_to_cart === true,
          };
          const quickAddEnabled = canUseStorefrontQuickAdd({
            availabilityContext: {
              cart: state.cart,
              usedComponents: state.usedComponents,
            },
            categories: state.categories,
            product: quickAddProduct,
            promotions: state.discounts,
            settings: state.settings,
            smartPromos: state.smartPromos,
          });
          return quickAddEnabled ? (
            <QuickAddToCart
              key={`storefront-quick-add-${quickAddProduct.id}`}
              fromCheckout={props.fromCheckout}
              fromRecommendations={props.fromRecommendations}
              isFilter={props.isFilter}
              isSearch={props.isSearch}
              product={quickAddProduct}
              setIsSelected={() => {}}
              type={quickAddType}
            />
          ) : shouldShowStorefrontDetailsAction({
              product: quickAddProduct,
              settings: state.settings,
            }) ? (
            <Button
              aria-label={
                productDetailsAction(quickAddProduct, state.language)
              }
              color="primary"
              disableElevation
              disableRipple
              dir={state.language === "arabic" ? "rtl" : "ltr"}
              fullWidth
              onClick={() => activateProduct(selectedProduct, variantState)}
              size="small"
              startIcon={<AddOutlined />}
              style={{
                border: `1px solid ${state.settings.dark || "#0099cc"}`,
                color: state.settings.dark || "#0099cc",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 10,
                minHeight: 44,
                textTransform: "none",
              }}
              variant="outlined"
            >
              {productDetailsAction(quickAddProduct, state.language)}
            </Button>
          ) : null;
        }}
        availabilityContext={{
          cart: state.cart,
          usedComponents: state.usedComponents,
        }}
        favoriteControl={(selectedProduct, { defaultChildId }) =>
          shouldShowFavoriteControl({
            settings: state.settings,
            user: state.user,
          }) ? (
            <FavoriteProductIcon
              productId={defaultChildId || selectedProduct.id}
            />
          ) : null
        }
        getPriceQuote={getPriceQuote}
        language={state.language}
        merchantTag={(selectedProduct) => {
          const tag = resolveMerchantTag({
            language: state.language,
            product: selectedProduct,
            settings: state.settings,
            tags: state.tags,
          });
          return tag ? <MerchantTagBadge {...tag} /> : null;
        }}
        onActivate={activateProduct}
        preset={foundation.preset}
        preparationTime={(selectedProduct) => {
          const preparation = resolvePreparationTime({
            product: selectedProduct,
            settings: state.settings,
            surface,
          });
          if (!preparation) return null;
          const label = preparation.onSelection
            ? state.language === "arabic"
              ? "حسب الاختيار"
              : "On selection"
            : getPrepTime(preparation.minutes, state.language);
          return (
            <PreparationTimeToken label={label} language={state.language} />
          );
        }}
        product={props.product}
        settings={state.settings}
        stockWarning={(selectedProduct, { availability }) => {
          const warning = resolveStockWarning({
            availability,
            product: selectedProduct,
          });
          return warning ? (
            <StockWarningMessage
              inventoryOnHand={warning.inventoryOnHand}
              language={state.language}
              messageValue={warning.messageValue}
            />
          ) : null;
        }}
        surface={props.isSearch ? "search" : props.storefrontSurface || surface}
      />
    );
  });

  const StorefrontProductCardAdapter = (props) => {
    const foundation = useStorefrontFoundation();
    const settings = useSelector(
      (rootState) => rootState.store?.store?.settings || {},
    );
    const enabled =
      foundation.enabled || isStorefrontFoundationEnabled(settings);
    const resolvedFoundation = foundation.enabled
      ? foundation
      : {
          enabled,
          preset: enabled ? resolveStorefrontPreset(settings) : null,
        };
    const product = props.product;
    const canRenderFoundation =
      product &&
      typeof product === "object" &&
      (product.id !== undefined || Boolean(product.slug));
    if (!resolvedFoundation.enabled || !canRenderFoundation) {
      return <LegacyComponent {...props} />;
    }
    return (
      <FoundationProductCard
        {...props}
        foundation={resolvedFoundation}
      />
    );
  };

  StorefrontProductCardAdapter.displayName = `withStorefrontProductCard(${
    LegacyComponent.displayName || LegacyComponent.name || "Component"
  })`;
  return StorefrontProductCardAdapter;
};

export default withStorefrontProductCard;
